import { useGetDashBoard } from 'hooks/api/useGetDashboard'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { useLayout } from 'hooks/useLayout'
import { colorPrimitives } from 'components/Theme'
import { useNavigate } from 'react-router-dom'
import { usePatronCheckInsDateRangeState } from 'stores/usePatronCheckInsDateRange'
import WeeklySnapshot from 'assets/WeeklySnapshot.png'
import { SystemBannerDisplay } from 'components/SystemBanners/SystemBannerDisplay'
import { useGetAllActiveSystemBanners } from 'hooks/api/useGetAllActiveSystemBanners'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { getLEPermissions } from 'src/navigation/pathPermissions'
import { useSetMe } from 'stores/useMeQuery'

interface CheckInMetricProps {
  label: string
  metric: number
  isMobile: boolean
}

const CheckInMetric = ({ label, metric, isMobile }: CheckInMetricProps) => {
  const navigate = useNavigate()
  const { setStartDate, setEndDate } = usePatronCheckInsDateRangeState()

  return (
    <Stack
      onClick={async () => {
        const now = new Date()
        const currentYear = now.getFullYear()
        const currentMonth = now.getMonth()
        const currentDay = now.getDate()
        if (label === 'Check Ins Today') {
          setStartDate(
            new Date(currentYear, currentMonth, currentDay, 0, 0, 0, 0)
          )
          setEndDate(
            new Date(currentYear, currentMonth, currentDay, 23, 59, 59, 999)
          )
        } else if (
          label === 'Check Ins this Month' ||
          label === 'VIP Check Ins this Month'
        ) {
          setStartDate(new Date(currentYear, currentMonth, 1, 0, 0, 0, 0))
          setEndDate(
            new Date(currentYear, currentMonth, currentDay, 23, 59, 59, 999)
          )
        } else if (label === 'YTD Check Ins') {
          setStartDate(new Date(currentYear, 0, 1, 0, 0, 0, 0))
          setEndDate(
            new Date(currentYear, currentMonth, currentDay, 23, 59, 59, 999)
          )
        }

        if (
          label === 'Check Ins Today' ||
          label === 'Check Ins this Month' ||
          label === 'YTD Check Ins'
        ) {
          await navigate(`/PatronCheckIns`)
        } else if (
          label === 'Average Age' ||
          label === '% Female Patrons' ||
          label === '% Male Patrons'
        ) {
          await navigate(`/PlayerPaybackReports/PatronStats`)
        }
      }}
      className={
        isMobile
          ? 'bg-white rounded-lg shadow-md mx-1 p-2 pb-5 w-full mb-1'
          : `bg-white rounded-lg shadow-md mx-1 p-2 pb-5 w-1/2 md:w-full md:rounded-none md:shadow-none md:mx-0 md:p-0 md:px-6 ${
              label !== 'Total Patrons' && label !== 'VIP Check Ins this Month'
                ? `cursor-pointer hover:bg-gray-100`
                : ''
            }`
      }
    >
      <Typography align="center" variant="caption">
        {label}
      </Typography>
      <Typography
        align="center"
        variant="h2"
        data-testid={`Dashboard-${label}-Metric`}
      >
        {metric}
      </Typography>
    </Stack>
  )
}

interface CheckInMetricsProps {
  dailyCheckIns: number
  monthlyCheckIns: number
  monthlyVipCheckIns: number
  yearlyCheckIns: number
  uniquePatrons: number
  averageAge: number
  femalePatronsPercentage: number
  malePatronsPercentage: number
}

const CheckInDivider = () => (
  <Divider orientation="vertical" flexItem className="md:visible invisible" />
)

const CheckInMetrics = ({
  dailyCheckIns,
  monthlyCheckIns,
  monthlyVipCheckIns,
  yearlyCheckIns,
  uniquePatrons,
  averageAge,
  femalePatronsPercentage,
  malePatronsPercentage,
}: CheckInMetricsProps) => {
  const isMobile = useLayout().isMobile

  return isMobile ? (
    <Stack
      direction="column"
      justifyContent="flex-start"
      className="w-[90vw] md:w-full md:bg-white md:rounded-lg md:shadow-md md:py-4"
    >
      <CheckInMetric
        label="Check Ins Today"
        metric={dailyCheckIns}
        isMobile={isMobile}
      />
      <CheckInDivider />
      <CheckInMetric
        label="Check Ins this Month"
        metric={monthlyCheckIns}
        isMobile={isMobile}
      />
      <CheckInDivider />
      <CheckInMetric
        label="VIP Check Ins this Month"
        metric={monthlyVipCheckIns}
        isMobile={isMobile}
      />
      <CheckInDivider />
      <CheckInMetric
        label="YTD Check Ins"
        metric={yearlyCheckIns}
        isMobile={isMobile}
      />
      <CheckInMetric
        label="Total Patrons"
        metric={uniquePatrons}
        isMobile={isMobile}
      />
      <CheckInDivider />
      <CheckInMetric
        label="Average Age"
        metric={averageAge}
        isMobile={isMobile}
      />
      <CheckInDivider />
      <CheckInMetric
        label="% Female Patrons"
        metric={femalePatronsPercentage}
        isMobile={isMobile}
      />
      <CheckInDivider />
      <CheckInMetric
        label="% Male Patrons"
        metric={malePatronsPercentage}
        isMobile={isMobile}
      />
    </Stack>
  ) : (
    <Stack direction="column" gap={0.5} flexGrow={1}>
      <Stack
        direction="row"
        justifyContent="flex-start"
        className="w-[175vw] md:w-full md:bg-white md:rounded-lg md:shadow-md md:py-4"
      >
        <CheckInMetric
          label="Check Ins Today"
          metric={dailyCheckIns}
          isMobile={isMobile}
        />
        <CheckInDivider />
        <CheckInMetric
          label="Check Ins this Month"
          metric={monthlyCheckIns}
          isMobile={isMobile}
        />
        <CheckInDivider />
        <CheckInMetric
          label="VIP Check Ins this Month"
          metric={monthlyVipCheckIns}
          isMobile={isMobile}
        />
        <CheckInDivider />
        <CheckInMetric
          label="YTD Check Ins"
          metric={yearlyCheckIns}
          isMobile={isMobile}
        />
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-start"
        className="w-[175vw] md:w-full md:bg-white md:rounded-lg md:shadow-md md:py-4"
      >
        <CheckInMetric
          label="Total Patrons"
          metric={uniquePatrons}
          isMobile={isMobile}
        />
        <CheckInDivider />
        <CheckInMetric
          label="Average Age"
          metric={averageAge}
          isMobile={isMobile}
        />
        <CheckInDivider />
        <CheckInMetric
          label="% Female Patrons"
          metric={femalePatronsPercentage}
          isMobile={isMobile}
        />
        <CheckInDivider />
        <CheckInMetric
          label="% Male Patrons"
          metric={malePatronsPercentage}
          isMobile={isMobile}
        />
      </Stack>
    </Stack>
  )
}

interface DashBoardHeaderCorporateAccountMetricsProps {
  corporateAccountId: number | undefined
  corporateAccountName: string | undefined
}

const DashBoardHeaderCorporateAccountMetrics = ({
  corporateAccountId,
  corporateAccountName,
}: DashBoardHeaderCorporateAccountMetricsProps) => {
  const dashboardQuery = useGetDashBoard({
    corporateAccountId,
  })
  const { isMobile } = useLayout()

  if (corporateAccountId === undefined) {
    return <></>
  }

  if (dashboardQuery.isPending) {
    return <ActivityIndicator />
  }

  if (dashboardQuery.isError) {
    return <ErrorIndicator />
  }

  return (
    <>
      <Box mt={{ xs: '21px', sm: '32px' }} mb={'10px'}>
        <Typography variant="h4">{`Player PAYBACK\u00AE`}</Typography>
      </Box>

      <Box className="mb-3">
        <Typography variant="body-3" color={colorPrimitives.darkGray}>
          Viewing stats for {corporateAccountName}
        </Typography>
      </Box>

      <Box className="relative" mb={'100px'}>
        {!isMobile && (
          <Box className="absolute -left-4 px-3 top-0 w-screen pb-2 overflow-x-auto md:overflow-visible md:left-0 md:px-0 md:w-full">
            <CheckInMetrics
              dailyCheckIns={dashboardQuery.data.numCheckinsForToday}
              monthlyCheckIns={dashboardQuery.data.numCheckinsForThisMonth}
              monthlyVipCheckIns={
                dashboardQuery.data.numVipCheckinsForThisMonth
              }
              yearlyCheckIns={dashboardQuery.data.numCheckinsForThisYear}
              uniquePatrons={dashboardQuery.data.numUniquePatrons}
              averageAge={dashboardQuery.data.averageAge}
              femalePatronsPercentage={
                dashboardQuery.data.femalePatronsPercentage
              }
              malePatronsPercentage={dashboardQuery.data.malePatronsPercentage}
            />
          </Box>
        )}
        {isMobile && (
          <CheckInMetrics
            dailyCheckIns={dashboardQuery.data.numCheckinsForToday}
            monthlyCheckIns={dashboardQuery.data.numCheckinsForThisMonth}
            monthlyVipCheckIns={dashboardQuery.data.numVipCheckinsForThisMonth}
            yearlyCheckIns={dashboardQuery.data.numCheckinsForThisYear}
            uniquePatrons={dashboardQuery.data.numUniquePatrons}
            averageAge={dashboardQuery.data.averageAge}
            femalePatronsPercentage={
              dashboardQuery.data.femalePatronsPercentage
            }
            malePatronsPercentage={dashboardQuery.data.malePatronsPercentage}
          />
        )}
      </Box>
    </>
  )
}

interface DashBoardHeaderProps {
  corporateAccountId: number | undefined
  corporateAccountName: string | undefined
}

export const DashBoardHeader = ({
  corporateAccountId,
  corporateAccountName,
}: DashBoardHeaderProps) => {
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const isMobile = useLayout().isMobile
  const useGetAllBannersQuery = useGetAllActiveSystemBanners()

  const allBanners = useGetAllBannersQuery.data

  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  // Get the LE permissions
  const lePermissions = getLEPermissions(
    meQuery?.data?.licensedEstablishments,
    meQuery?.data?.user?.licensedEstablishments,
    selectedCorporateAccountId
  )

  return (
    <Stack
      className={
        isMobile
          ? 'px-4 pt-14 pb-10 bg-[#f7f7f6] md:px-10'
          : 'px-4 pt-14 pb-24 bg-[#f7f7f6] md:px-10'
      }
    >
      <Typography variant="h2">
        Welcome Back, {meQuery.data?.user?.firstName ?? 'Unknown'}!
      </Typography>

      <Stack className="pt-8 ">
        <Box className="pb-8 flex flex-col gap-2">
          <SystemBannerDisplay systemBanners={allBanners ?? []} />
        </Box>
        {lePermissions?.[0]?.gamingReportsPermission && (
          <>
            <Box mt={{ xs: '21px', sm: '32px' }} mb={'10px'}>
              <Typography variant="h4">{`Gaming Reports`}</Typography>
            </Box>
            <Box
              mb={2}
              component="a"
              href="/GamingReports/WeeklySnapshotLocationSelect"
              sx={{
                display: 'block',
                textDecoration: 'none',
                width: 'max-content',
                // setup for overlay effect when the user hovers or focuses on the image
                position: 'relative',
                '.overlay': {
                  opacity: 1,
                },
              }}
            >
              <img
                alt="Weekly Snapshot Reports"
                width="360"
                height="480"
                src={WeeklySnapshot}
                style={{ display: 'block' }}
              />
              {/* Overlay box that appears on hover/focus of the image */}
              <Box
                className="overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  padding: 2,
                }}
              >
                {/* Text inside the overlay box */}
                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  Click to view report
                </Typography>
              </Box>
            </Box>
          </>
        )}
        <Stack className="pt-8">
          <DashBoardHeaderCorporateAccountMetrics
            corporateAccountId={corporateAccountId}
            corporateAccountName={corporateAccountName}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
